import React, { useEffect, useState, useCallback } from "react";
import { Pagination, Form } from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";

import { getDatasets } from "../../../services/CkanApiService";
import { getBpsDatasets } from "../../../services/BpsApiService";

// ─── Helpers ──────────────────────────────────────────────────────────────────
// const formatYear = (dateString) => {
//     if (!dateString) return "-";
//     try {
//         const date = new Date(dateString);
//         return isNaN(date) ? "-" : date.getFullYear();
//     } catch {
//         return "-";
//     }
// };

/**
 * Tentukan mode fetch berdasarkan orgName:
 * - "bps"      → fetch BPS only
 * - string lain → fetch CKAN only (filter by org)
 * - null/undefined → fetch keduanya (halaman Kumpulan Data umum)
 */
const getFetchMode = (orgName) => {
    if (!orgName) return "all";
    if (orgName === "bps") return "bps";
    return "ckan";
};

// ─── Component ────────────────────────────────────────────────────────────────
const ListDataKumpulanDataCompOpenData = React.memo(({ orgName: orgNameProp }) => {
    const { orgName: orgNameParam } = useParams();
    const location = useLocation();

    // Prioritas: prop → useParams → location.state
    const selectedOrg =
        orgNameProp ||
        orgNameParam ||
        location.state?.organisasi ||
        null;

    const fetchMode = getFetchMode(selectedOrg);

    // ── States ───────────────────────────────────────────────────────────────
    const [items, setItems]             = useState([]);
    const [loading, setLoading]         = useState(true);
    const [totalItems, setTotalItems]   = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages]   = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem   = Math.min(currentPage * itemsPerPage, totalItems);

    // ── Fetch ────────────────────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let datasets = [];
            let total    = 0;

            if (fetchMode === "bps") {
                // ── BPS only ─────────────────────────────────────────────
                const bpsRes = await getBpsDatasets(currentPage, itemsPerPage);
                datasets     = bpsRes.datasets ?? [];
                total        = bpsRes.total    ?? 0;

            } else if (fetchMode === "ckan") {
                // ── CKAN only, filter by org ──────────────────────────────
                const ckanRes = await getDatasets(currentPage, itemsPerPage, selectedOrg);
                datasets      = ckanRes.datasets ?? [];
                total         = ckanRes.total    ?? 0;

            } else {
                // ── Semua (halaman Kumpulan Data umum) ───────────────────
                const [ckanRes, bpsRes] = await Promise.all([
                    getDatasets(currentPage, itemsPerPage, null),
                    getBpsDatasets(currentPage, itemsPerPage),
                ]);
                datasets = [
                    ...(ckanRes.datasets ?? []),
                    ...(bpsRes.datasets  ?? []),
                ];
                total = (ckanRes.total ?? 0) + (bpsRes.total ?? 0);
            }

            // Client-side search filter (hanya untuk mode "all")
            if (searchQuery.trim() && fetchMode === "all") {
                const q = searchQuery.toLowerCase();
                datasets = datasets.filter((item) =>
                    item.title?.toLowerCase().includes(q) ||
                    item.author?.toLowerCase().includes(q)
                );
                total = datasets.length;
            }

            setItems(datasets);
            setTotalItems(total);
            setTotalPages(Math.ceil(total / itemsPerPage));
        } catch (err) {
            console.error("fetchData:", err.message);
            setItems([]);
            setTotalItems(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, selectedOrg, fetchMode, searchQuery]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleSearch = (query) => {
        setSearchQuery(query);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    // ── Pagination Items ─────────────────────────────────────────────────────
    const createPaginationItems = () => {
        const maxVisible = 5;
        let start = Math.max(1, currentPage - 2);
        let end   = Math.min(totalPages, start + maxVisible - 1);
        if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

        const pages = [];

        if (start > 1) {
            pages.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>
            );
            if (start > 2) pages.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
        }

        for (let i = start; i <= end; i++) {
            pages.push(
                <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
                    {i}
                </Pagination.Item>
            );
        }

        if (end < totalPages) {
            if (end < totalPages - 1) pages.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            pages.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return pages;
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <section className="d-flex align-items-center mt-5 mb-5">
            <div className="container">
                <div className="row content">
                    <div className="col-md-12">

                        {/* Header Controls */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="d-flex align-items-center">
                                <span>Show </span>
                                <Form.Select
                                    size="sm"
                                    className="mx-2"
                                    style={{ width: "70px", cursor: "pointer" }}
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </Form.Select>
                                <span> entries</span>
                            </div>

                            <div className="d-flex align-items-center">
                                <span className="me-2">Search:</span>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    style={{ width: "250px" }}
                                />
                            </div>
                        </div>

                        {/* Tabel */}
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped table-hover align-middle">
                                <thead style={{ backgroundColor: "#f8f9fa", color: "#333" }} className="text-center">
                                    <tr>
                                        <th width="5%">No</th>
                                        <th width="45%">Judul Data</th>
                                        <th width="20%">Perangkat Daerah</th>
                                        {/* <th width="10%">Tahun</th> */}
                                        <th width="10%">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">Loading Data...</td>
                                        </tr>
                                    ) : items.length > 0 ? (
                                        items.map((data, idx) => (
                                            <tr key={data.name ?? idx}>
                                                <td className="text-center">{startItem + idx}</td>
                                                <td>{data.title ?? "-"}</td>
                                                <td>{data.organization?.title ?? data.author ?? "BPS"}</td>
                                                {/* <td className="text-center">{formatYear(data.metadata_modified)}</td> */}
                                                <td className="text-center">
                                                    <Link
                                                        to={
                                                            data.source === "CKAN"
                                                                ? `/open_data/kumpulan_data/${data.name}`
                                                                : `/open_data/bps_data/${data.name}`
                                                        }
                                                        className="btn btn-sm btn-success text-white"
                                                        state={{ data }}
                                                        title="Lihat Detail"
                                                    >
                                                        <i className="bx bx-file"></i> Detail
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4 text-muted">
                                                {searchQuery
                                                    ? "Tidak ditemukan dataset yang sesuai pencarian"
                                                    : "No Data Found"}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer Controls */}
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted small">
                                Showing {items.length === 0 ? 0 : startItem} to {endItem} of {totalItems} entries
                            </div>

                            {items.length > 0 && totalPages > 1 && (
                                <Pagination size="sm" className="mb-0">
                                    <Pagination.Prev
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </Pagination.Prev>
                                    {createPaginationItems()}
                                    <Pagination.Next
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </Pagination.Next>
                                </Pagination>
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </section>
    );
});

export default ListDataKumpulanDataCompOpenData;